import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { InboxDto } from '@app/core/model';
import { InboundFaxApiService, LookupService, NotificationService } from '@app/core/service';
import { FormDialogComponent } from '@app/shared/components';
import { FormDialogConfig, InboundFaxesTableModel } from '@app/shared/models';
import { DialogConfig } from '@app/shared/utility';
import { ToastMessageOptions, SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { of } from 'rxjs';
import { InboundFaxesService } from './inbound-faxes.service';

@Injectable()
export class ArchiveFaxService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly inboundFaxApiService = inject(InboundFaxApiService);
  private readonly inboundFaxesService = inject(InboundFaxesService);
  private readonly lookupService = inject(LookupService);
  private readonly notificationService = inject(NotificationService);

  private readonly archiveSuccessMessage: ToastMessageOptions = {
    detail: 'Fax record has been moved to the archived faxes view.',
    summary: 'Successfully Archived Fax',
  };

  private readonly archiveErrorMessage: ToastMessageOptions = {
    detail: 'There was an issue archiving the fax. Please try again later.',
    summary: 'Unable to Archive Fax',
  };

  public async handleArchiveClick(target: InboundFaxesTableModel, inbox: InboxDto): Promise<void> {
    const references = await this.getArchivedReasonReferences();
    const formDialogConfigs: FormDialogConfig[] = [
      {
        label: 'Reason To Archive:',
        type: 'dropdown',
        formControlName: 'archivedReasonId',
        options: references,
        validators: [Validators.required],
      },
    ];

    this.dialogService
      .open(
        FormDialogComponent,
        DialogConfig('Archive Fax', {
          submitLabel: 'Archive',
          contextText:
            'Add a reason to archive the fax. Archived fax records will be hidden from the inbound faxes view.',
          formDialogConfigs: of(formDialogConfigs),
        })
      )
      .onClose.subscribe((res: object) => {
        if (res) {
          this.updateArchive(
            target.inboundFaxId,
            res['archivedReasonId'],
            inbox,
            false,
            this.archiveSuccessMessage,
            this.archiveErrorMessage
          );
        }
      });
  }

  public handleRestoreArchiveClick(target: InboundFaxesTableModel, inbox: InboxDto): void {
    if (!target.restoreArchive) return;

    const dialogConfig = [
      {
        label: 'Are you sure you want to restore the selected fax?',
        type: '',
        formControlName: '',
      },
    ];

    this.dialogService
      .open(
        FormDialogComponent,
        DialogConfig('Restore Fax', {
          submitLabel: 'Restore',
          formDialogConfigs: of(dialogConfig),
        })
      )
      .onClose.subscribe((res: object) => {
        if (res) {
          this.updateArchive(
            target.inboundFaxId,
            null,
            inbox,
            true,
            {
              detail: `Fax record has been moved to ${inbox.label}`,
              summary: 'Successfully Restored Fax',
            },
            {
              detail: 'There was an issue restoring the fax. Please try again later.',
              summary: 'Unable to Restore Fax',
            }
          );
        }
      });
  }

  public async setStatusReasonAndUpdate(
    inboundFax: InboundFaxesTableModel,
    inbox: InboxDto,
    statusReason: string
  ): Promise<void> {
    this.inboundFaxesService.isLoadingTableData$.next(true);

    const references = await this.getArchivedReasonReferences();
    const foundReason: SelectItem = references.find((x) => x.label == statusReason);

    if (foundReason) {
      this.updateArchive(
        inboundFax.inboundFaxId,
        foundReason.value,
        inbox,
        false,
        this.archiveSuccessMessage,
        this.archiveErrorMessage
      );
    } else {
      this.onError(this.archiveErrorMessage);
    }
  }

  private updateArchive(
    inboundFaxId: string,
    updateId: string,
    inbox: InboxDto,
    showArchive: boolean,
    successMessage: ToastMessageOptions,
    errorMessage: ToastMessageOptions
  ): void {
    this.inboundFaxesService.isLoadingTableData$.next(true);

    this.inboundFaxApiService
      .updateArchive(inboundFaxId, { updateId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess(successMessage.summary, successMessage.detail);
          this.inboundFaxesService.reload(inbox, showArchive);
        },
        error: () => {
          this.onError(errorMessage);
        },
      });
  }

  private onError(message: ToastMessageOptions): void {
    this.notificationService.showError(message.summary, message.detail);
    this.inboundFaxesService.isLoadingTableData$.next(false);
  }

  private async getArchivedReasonReferences(): Promise<SelectItem[]> {
    return (await this.lookupService.getArchivedReasonReferences()).map((x) => ({
      label: x.description,
      value: x.referenceByEntityId,
    }));
  }
}
